import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { NavListCommonProps } from 'src/app/components/nav/nav.component';
import { environment } from 'src/environments/environment';
import { FunctionalityCode } from '../_enums/functionality.code';
import { GroupBy } from '../_enums/groupby';
import { SiteMapUrl } from '../_enums/sitemap.url';
import { Energie } from '../_models/energie';
import { AuthService } from './auth.service';
import { DredgingService } from './dredging.service';

@Injectable({
  providedIn: 'root'
})
export class EnergieService {
  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private router: Router,
    private translate: TranslateService,
    private dredgingService: DredgingService
  ) {}

  getAllForVehicle(groupBy?: string, versionId?: string) {
    let params = new HttpParams();
    params = groupBy ? params.set('groupBy', groupBy) : params;
    params = versionId ? params.set('versionId', versionId) : params;
    return this.http.get<Energie[]>(`${environment.apiUrl}/Energies/forVehicle`, { params, headers: this.authService.headers });
  }

  getAllForTransportation(groupBy?: string, versionId?: string) {
    let params = new HttpParams();
    params = groupBy ? params.set('groupBy', groupBy) : params;
    params = versionId ? params.set('versionId', versionId) : params;
    return this.http.get<Energie[]>(`${environment.apiUrl}/Energies/forTransportation`, { params, headers: this.authService.headers });
  }

  getForVehicle(id: string) {
    return this.http.get<Energie>(`${environment.apiUrl}/Energies/forVehicle/${id}`, { headers: this.authService.headers });
  }

  createForVehicle(energie: Energie) {
    return this.http.post<Energie>(`${environment.apiUrl}/Energies/createForVehicle`, energie, { headers: this.authService.headers });
  }

  updateForVehicle(id: string, energie: Energie) {
    return this.http.put<Energie>(`${environment.apiUrl}/Energies/updateForVehicle/${id}`, energie, { headers: this.authService.headers });
  }

  deleteForVehicle(id: string) {
    return this.http.delete(`${environment.apiUrl}/Energies/deleteForVehicle/${id}`, { headers: this.authService.headers });
  }

  duplicateForVehicle(id: string, code: string) {
    return this.http.post<Energie>(`${environment.apiUrl}/Energies/duplicateForVehicle?id=${id}&code=${encodeURIComponent(code)}`, null, { headers: this.authService.headers });
  }

  getForTransportation(id: string) {
    return this.http.get<Energie>(`${environment.apiUrl}/Energies/forTransportation/${id}`, { headers: this.authService.headers });
  }

  createForTransportation(energie: Energie) {
    return this.http.post<Energie>(`${environment.apiUrl}/Energies/createForTransportation`, energie, { headers: this.authService.headers });
  }

  updateForTransportation(id: string, energie: Energie) {
    return this.http.put<Energie>(`${environment.apiUrl}/Energies/updateForTransportation/${id}`, energie, { headers: this.authService.headers });
  }

  deleteForTransportation(id: string) {
    return this.http.delete(`${environment.apiUrl}/Energies/deleteForTransportation/${id}`, { headers: this.authService.headers });
  }

  duplicateForTransportation(id: string, code: string) {
    return this.http.post<Energie>(`${environment.apiUrl}/Energies/duplicateForTransportation?id=${id}&code=${encodeURIComponent(code)}`, null, { headers: this.authService.headers });
  }

  getNavConfig({ perimeterId }: NavListCommonProps) {
    return {
      header: {
        title: 'ressource_list_title_energies',
        actions: [
          ...(this.authService.accessesValues.includes(FunctionalityCode.ManageVehicleEnergie)
            ? [
                {
                  label: 'ressource_list_action_create_vehicle_energy',
                  onClick: () => {
                    this.router.navigate([`${SiteMapUrl.MenuEnergie}/vehicle/new`, 0], { state: { perimeterId } });
                  }
                }
              ]
            : []),
          ...(this.authService.accessesValues.includes(FunctionalityCode.ManageEnergie)
            ? [
                {
                  label: 'ressource_list_action_create_transport_energy',
                  onClick: () => {
                    this.router.navigate([`${SiteMapUrl.MenuEnergie}/transportation/new`, 0], { state: { perimeterId } });
                  }
                }
              ]
            : []),
          ...(this.authService.accessesValues.includes(FunctionalityCode.ManageDredgingEnergie)
            ? [
                {
                  label: 'ressource_list_action_create_maritime_energy',
                  onClick: () => {
                    this.router.navigate([`${SiteMapUrl.MenuDredgingFuel}/new`, 0], { state: { perimeterId } });
                  }
                }
              ]
            : [])
        ],
        filters: [
          { label: 'ressource_list_filter_by_family', value: GroupBy.family },
          { label: 'ressource_list_filter_by_favorite', value: GroupBy.favorite }
        ],
        defaultFilter: GroupBy.family,
        onFilterChange: () => {}
      },
      getDataSource: (groupBy?: GroupBy) => {
        return forkJoin({
          vehicle: this.getAllForVehicle(groupBy),
          transportation: this.getAllForTransportation(groupBy),
          dredging: this.dredgingService.getAll(groupBy)
        }).pipe(
          map(({ vehicle, transportation, dredging }) => {
            const vehicleItems = (vehicle || []).filter((e) => e.displayName).map((e) => ({ ...e, _energyType: 'vehicle' as const }));
            const transportItems = (transportation || []).filter((e) => e.displayName).map((e) => ({ ...e, _energyType: 'transportation' as const }));
            const dredgingItems = (dredging || []).filter((e: any) => e.displayName).map((e: any) => ({ ...e, _energyType: 'dredging' as const }));
            const categories: { key: string; value: (Energie & { _energyType: string })[] }[] = [];
            if (vehicleItems.length > 0) categories.push({ key: this.translate.instant('energy_category_vehicle'), value: vehicleItems });
            if (transportItems.length > 0) categories.push({ key: this.translate.instant('energy_category_transportation'), value: transportItems });
            if (dredgingItems.length > 0) categories.push({ key: this.translate.instant('energy_category_maritime'), value: dredgingItems });
            return categories;
          })
        );
      },
      getItemConfig: (node: Energie & { _energyType?: string }) => {
        if (node._energyType === 'dredging') {
          return {
            displayName: `${node.displayName} [${node.code}]`,
            url: `${SiteMapUrl.MenuDredgingFuel}/${node.resourceId}`,
            itemType: 'default',
            state: {
              resourceId: node.resourceId,
              perimeterId: perimeterId
            }
          };
        }
        const typePath = node._energyType ? `${node._energyType}/` : '';
        return {
          displayName: `${node.displayName} [${node.code}]`,
          url: `${SiteMapUrl.MenuEnergie}/${typePath}${node.resourceId}`,
          itemType: 'default',
          state: {
            resourceId: node.resourceId,
            perimeterId: perimeterId
          }
        };
      }
    };
  }
}
